import React, { useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { Percent, Plus, Trash2, AlertTriangle, ThumbsUp, ThumbsDown, AlertCircle } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const formatCurrency = (val) => `$${(val || 0).toFixed(2)}`;

export default function SpecialsCalculator() {
  const { recipes, specials, addSpecial, deleteSpecial } = useApp();
  const [form, setForm] = useState({
    recipeId: '',
    regularPrice: 0,
    specialPrice: 0,
    totalCostPerItem: 0,
    expectedVolumeIncrease: 20,
  });

  const handleRecipeSelect = (recipeId) => {
    const recipe = recipes.find(r => r.id === recipeId);
    if (recipe) {
      setForm(f => ({
        ...f,
        recipeId,
        regularPrice: recipe.menuPrice || 0,
        totalCostPerItem: recipe.totalCost || 0,
      }));
    } else {
      setForm(f => ({ ...f, recipeId }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const regularPrice = Number(form.regularPrice) || 0;
    const specialPrice = Number(form.specialPrice) || 0;
    const totalCost = Number(form.totalCostPerItem) || 0;
    const volumeIncrease = Number(form.expectedVolumeIncrease) || 0;

    const regularProfit = regularPrice - totalCost;
    const specialProfit = specialPrice - totalCost;
    const profitLost = regularProfit - specialProfit;
    const requiredVolumeIncrease = specialProfit > 0 ? (regularProfit / specialProfit - 1) * 100 : Infinity;

    let recommendation = 'Bad deal';
    let recColor = '#E23D28';
    if (specialProfit <= 0) {
      recommendation = "Bad deal, you're buying customers";
      recColor = '#E23D28';
    } else if (requiredVolumeIncrease <= volumeIncrease) {
      recommendation = 'Good special';
      recColor = '#2E7D32';
    } else if (requiredVolumeIncrease <= volumeIncrease * 1.5) {
      recommendation = 'Risky special';
      recColor = '#FFC107';
    } else {
      recommendation = "Bad deal, you're buying customers";
      recColor = '#E23D28';
    }

    const recipe = recipes.find(r => r.id === form.recipeId);

    addSpecial({
      recipeName: recipe?.name || 'Custom Item',
      regularPrice,
      specialPrice,
      totalCostPerItem: totalCost,
      expectedVolumeIncrease: volumeIncrease,
      regularProfit,
      specialProfit,
      profitLost,
      requiredVolumeIncrease,
      recommendation,
      recColor,
    });

    setForm({ recipeId: '', regularPrice: 0, specialPrice: 0, totalCostPerItem: 0, expectedVolumeIncrease: 20 });
  };

  const getRecIcon = (rec) => {
    if (rec.includes('Good')) return <ThumbsUp className="w-5 h-5" />;
    if (rec.includes('Risky')) return <AlertCircle className="w-5 h-5" />;
    return <ThumbsDown className="w-5 h-5" />;
  };

  return (
    <div className="page-enter p-4 md:p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 bg-[#FFC107] rounded-full border-2 border-[#2A2A2A] flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(42,42,42,1)]">
          <Percent className="w-5 h-5 text-[#2A2A2A]" />
        </div>
        <div>
          <h1 className="text-4xl sm:text-5xl text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }} data-testid="specials-title">
            Specials Calculator
          </h1>
          <p className="text-sm text-[#2A2A2A]/60">See if your special is a deal or a steal... from yourself.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Form */}
        <div className="pizza-card p-6" data-testid="specials-form">
          <h2 className="text-2xl text-[#2A2A2A] mb-4" style={{ fontFamily: "'Bebas Neue', cursive" }}>
            New Special
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            {recipes.length > 0 && (
              <div>
                <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Product (from recipes)</label>
                <Select value={form.recipeId} onValueChange={handleRecipeSelect}>
                  <SelectTrigger className="border-2 border-[#2A2A2A] rounded-lg w-full" data-testid="special-recipe-select">
                    <SelectValue placeholder="Select a recipe (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {recipes.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Regular Price ($)</label>
              <input type="number" step="0.01" min="0" value={form.regularPrice}
                onChange={e => setForm(f => ({ ...f, regularPrice: e.target.value }))}
                className="pizza-input w-full" data-testid="special-regular-price-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Special Price ($)</label>
              <input type="number" step="0.01" min="0" value={form.specialPrice}
                onChange={e => setForm(f => ({ ...f, specialPrice: e.target.value }))}
                className="pizza-input w-full" data-testid="special-price-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Total Cost Per Item ($)</label>
              <input type="number" step="0.01" min="0" value={form.totalCostPerItem}
                onChange={e => setForm(f => ({ ...f, totalCostPerItem: e.target.value }))}
                className="pizza-input w-full" data-testid="special-cost-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Expected Volume Increase %</label>
              <input type="number" step="0.01" min="0" value={form.expectedVolumeIncrease}
                onChange={e => setForm(f => ({ ...f, expectedVolumeIncrease: e.target.value }))}
                className="pizza-input w-full" data-testid="special-volume-input" />
            </div>
            <button type="submit" className="pizza-btn text-sm w-full" data-testid="special-submit-btn">
              <Plus className="w-4 h-4 inline mr-1" /> Analyze Special
            </button>
          </form>
        </div>

        {/* Results */}
        <div className="space-y-4" data-testid="specials-results">
          {specials.length === 0 ? (
            <div className="pizza-card p-8 text-center">
              <Percent className="w-12 h-12 text-[#2A2A2A]/20 mx-auto mb-3" />
              <h3 className="text-xl text-[#2A2A2A] mb-1" style={{ fontFamily: "'Bebas Neue', cursive" }}>No Specials Analyzed</h3>
              <p className="text-sm text-[#2A2A2A]/50">Add a special to see if it's worth running.</p>
            </div>
          ) : (
            specials.map(s => (
              <div key={s.id} className="pizza-card p-5" data-testid={`special-card-${s.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-xl text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{s.recipeName}</h3>
                    <p className="text-sm text-[#2A2A2A]/50">{formatCurrency(s.regularPrice)} &rarr; {formatCurrency(s.specialPrice)}</p>
                  </div>
                  <button onClick={() => deleteSpecial(s.id)} className="p-1.5 hover:bg-red-100 rounded" data-testid={`delete-special-${s.id}`}>
                    <Trash2 className="w-4 h-4 text-[#E23D28]" />
                  </button>
                </div>

                {/* Recommendation badge */}
                <div
                  className="flex items-center gap-2 rounded-lg px-3 py-2 mb-3"
                  style={{ background: s.recColor + '15', border: `2px solid ${s.recColor}`, color: s.recColor }}
                >
                  {getRecIcon(s.recommendation)}
                  <span className="font-bold text-sm uppercase" style={{ fontFamily: "'Bebas Neue', cursive", letterSpacing: '0.05em' }}>
                    {s.recommendation}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-[#2A2A2A]/50 text-xs">Regular Profit</p>
                    <p className="font-bold text-[#2E7D32]">{formatCurrency(s.regularProfit)}</p>
                  </div>
                  <div>
                    <p className="text-[#2A2A2A]/50 text-xs">Special Profit</p>
                    <p className={`font-bold ${s.specialProfit >= 0 ? 'text-[#2E7D32]' : 'text-[#E23D28]'}`}>{formatCurrency(s.specialProfit)}</p>
                  </div>
                  <div>
                    <p className="text-[#2A2A2A]/50 text-xs">Profit Lost/Item</p>
                    <p className="font-bold text-[#E23D28]">{formatCurrency(s.profitLost)}</p>
                  </div>
                  <div>
                    <p className="text-[#2A2A2A]/50 text-xs">Volume Increase Needed</p>
                    <p className="font-bold text-[#2A2A2A]">
                      {s.requiredVolumeIncrease === Infinity ? 'Impossible' : `${s.requiredVolumeIncrease.toFixed(0)}%`}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
